// src/pages/Dashboard.js — Home/welcome page with all four areas
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/api';

function StatGrid({ stats }) {
  return (
    <div className="stat-grid">
      {stats.map(s => (
        <div key={s.label} className={`stat-card stat-card-${s.colour}`}>
          <div className={`stat-icon stat-icon-${s.colour}`}>{s.icon}</div>
          <div className="stat-info">
            <div className="stat-value">{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Section({ icon, title, body, stats, primaryLabel, primaryPath, secondaryLabel, secondaryPath, navigate, loading }) {
  return (
    <div className="card" style={{ marginBottom: '20px' }}>
      <div className="card-header" style={{ paddingRight: '20px' }}>
        <div style={{ flex: 1, paddingRight: '16px' }}>
          <div style={{ fontWeight: 800, fontSize: '16px', color: 'var(--g900)', marginBottom: body ? '4px' : 0 }}>
            {icon} {title}
          </div>
          {body && <div style={{ fontSize: '13px', color: 'var(--g500)', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{body}</div>}
        </div>
        <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
          {secondaryLabel && <button className="btn btn-secondary btn-sm" onClick={() => navigate(secondaryPath)}>{secondaryLabel}</button>}
          <button className="btn btn-primary btn-sm" onClick={() => navigate(primaryPath)}>{primaryLabel}</button>
        </div>
      </div>
      <div className="card-body" style={{ paddingTop: '8px' }}>
        {loading ? <div style={{ textAlign: 'center', padding: '20px' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        : stats && <StatGrid stats={stats} />}
      </div>
    </div>
  );
}

export default function Dashboard({ isAdmin }) {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState(null);
  const [fileStats, setFileStats] = useState(null);
  const [credStats, setCredStats] = useState(null);
  const [fileReqStats, setFileReqStats] = useState(null);
  const [credReqStats, setCredReqStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tmpl, files, creds, fileReqs, credReqs] = await Promise.all([
        apiFetch('page-templates'),
        apiFetch('transfers/list'),
        apiFetch('credentials/list'),
        apiFetch('requests/list?type=file'),
        apiFetch('requests/list?type=credential'),
      ]);
      setTemplates(tmpl.templates || {});

      const ft = files.transfers || [];
      const ftExp = ft.filter(t => t.status !== 'active');
      setFileStats([
        { label: 'Total',      value: ft.length,                                         icon: '📊', colour: 'red'   },
        { label: 'Active',     value: ft.filter(t => t.status === 'active').length,       icon: '✅', colour: 'green' },
        { label: 'Downloaded', value: ftExp.filter(t => t.status === 'expired-downloaded').length, icon: '⬇️', colour: 'blue'  },
        { label: 'Expired',    value: ftExp.filter(t => t.status === 'expired-timeout').length,    icon: '⏱', colour: 'amber' },
      ]);

      const ct = creds.transfers || [];
      const ctExp = ct.filter(t => t.status !== 'active');
      setCredStats([
        { label: 'Total',   value: ct.length,                                               icon: '📊', colour: 'red'   },
        { label: 'Active',  value: ct.filter(t => t.status === 'active').length,             icon: '✅', colour: 'green' },
        { label: 'Viewed',  value: ctExp.filter(t => t.status === 'expired-downloaded').length, icon: '👁', colour: 'blue'  },
        { label: 'Expired', value: ctExp.filter(t => t.status === 'expired-timeout').length,    icon: '⏱', colour: 'amber' },
      ]);

      const fr = fileReqs.requests || [];
      setFileReqStats([
        { label: 'Total',     value: fr.length,                                      icon: '📊', colour: 'red'   },
        { label: 'Pending',   value: fr.filter(r => r.status === 'pending').length,   icon: '⏳', colour: 'green' },
        { label: 'Fulfilled', value: fr.filter(r => r.status === 'fulfilled').length, icon: '✓', colour: 'blue'  },
        { label: 'Expired',   value: fr.filter(r => r.status === 'expired').length,   icon: '⏱', colour: 'amber' },
      ]);

      const cr = credReqs.requests || [];
      setCredReqStats([
        { label: 'Total',     value: cr.length,                                      icon: '📊', colour: 'red'   },
        { label: 'Pending',   value: cr.filter(r => r.status === 'pending').length,   icon: '⏳', colour: 'green' },
        { label: 'Fulfilled', value: cr.filter(r => r.status === 'fulfilled').length, icon: '✓', colour: 'blue'  },
        { label: 'Expired',   value: cr.filter(r => r.status === 'expired').length,   icon: '⏱', colour: 'amber' },
      ]);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const intro      = templates?.['dashboard-intro']       || {};
  const filesBlock = templates?.['dashboard-files']       || {};
  const credsBlock = templates?.['dashboard-credentials'] || {};

  return (
    <div>
      <div className="page-eyebrow">Home</div>
      <h1 className="page-title">Dashboard</h1>

      {loading && !templates ? (
        <div style={{ textAlign: 'center', padding: '60px' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : (
        <>
          {(intro.title || intro.body) && (
            <div className="card" style={{ marginBottom: '20px', borderLeft: '4px solid var(--red)' }}>
              <div className="card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '24px' }}>
                <div style={{ flex: 1 }}>
                  {intro.title && <h2 style={{ fontSize: '18px', fontWeight: 800, color: 'var(--g900)', marginBottom: '8px', marginTop: 0 }}>{intro.title}</h2>}
                  {intro.body && <p style={{ fontSize: '14px', color: 'var(--g600)', lineHeight: '1.6', margin: 0, whiteSpace: 'pre-wrap' }}>{intro.body}</p>}
                </div>
                {isAdmin && (
                  <button className="btn btn-secondary btn-sm" style={{ flexShrink: 0, marginTop: '2px' }}
                    onClick={() => navigate('/admin/page-templates')}>✏️ Edit</button>
                )}
              </div>
            </div>
          )}

          <Section icon="📂" title={filesBlock.title || 'Send Files'} body={filesBlock.body}
            stats={fileStats} loading={loading}
            primaryLabel="⬆️ New File Transfer" primaryPath="/upload"
            secondaryLabel="View all" secondaryPath="/transfers"
            navigate={navigate} />

          <Section icon="📥" title="Request Files" body="Ask external parties to upload files to you — they receive a secure single-use upload link."
            stats={fileReqStats} loading={loading}
            primaryLabel="📥 New File Request" primaryPath="/filerequests/new"
            secondaryLabel="View all" secondaryPath="/filerequests"
            navigate={navigate} />

          <Section icon="🔏" title={credsBlock.title || 'Send Credentials'} body={credsBlock.body}
            stats={credStats} loading={loading}
            primaryLabel="🔏 New Credential Transfer" primaryPath="/credentials/new"
            secondaryLabel="View all" secondaryPath="/credentials"
            navigate={navigate} />

          <Section icon="🔓" title="Request Credentials" body="Ask external parties to submit passwords, API keys or sensitive text to you securely."
            stats={credReqStats} loading={loading}
            primaryLabel="🔓 New Credential Request" primaryPath="/credentialrequests/new"
            secondaryLabel="View all" secondaryPath="/credentialrequests"
            navigate={navigate} />
        </>
      )}
    </div>
  );
}
